

import { computed, defineComponent, onMounted, ref } from 'vue';
import {
  ConfigKeys,
  CreateUpdateAppConfig,
  createUpdateAppConfig,
  getAppConfig, testMailConfigApi
} from '@/api/appConfigApi';
import { useStore } from 'vuex';
import { key } from '@/store';
import BaseButton from '@/components/BaseButton.vue';
import RotateLoader from '@/components/RotateLoader.vue';
import { notification } from 'ant-design-vue';

const inputClass = 'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm';

export default defineComponent({
  name: 'AppConfigPage',
  components: { RotateLoader, BaseButton },
  setup: () => {
    const store = useStore(key);
    const isRequestingTestEmail = ref(false);
    const testEmail = ref('');
    const isEmailPasswordVisible = ref(false);
    const togglePasswordViewMode = () => {
      isEmailPasswordVisible.value = !isEmailPasswordVisible.value;
    };

    const activeTab = ref(1);

    const navButtonClass = (tab: number) => {
      const base = 'settings-nav-item w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';
      if (activeTab.value === tab) {
        return `${base} bg-sidebar-active text-white font-medium`;
      }
      return `${base} hover:bg-accent text-foreground`;
    };

    const toggleSidebar = () => {
      store.commit('setSidebarState', !store.state.layout.isSidebarOpen);
    };

    const isEmailConfigurationCompleted = computed(() => {
      return formValues.value[ConfigKeys.mail_host] !== ''
        && formValues.value[ConfigKeys.mail_from_address] !== ''
        && formValues.value[ConfigKeys.mail_password] !== ''
        && formValues.value[ConfigKeys.mail_port] !== ''
        && formValues.value[ConfigKeys.mail_username] !== ''
        && formValues.value[ConfigKeys.mail_encryption] !== '';
    });

    const formValues = ref({
      [ConfigKeys.mail_host]: '',
      [ConfigKeys.mail_from_address]: '',
      [ConfigKeys.mail_password]: '',
      [ConfigKeys.mail_port]: '',
      [ConfigKeys.mail_username]: '',
      [ConfigKeys.mail_encryption]: '',
      [ConfigKeys.mail_from_name]: '',
      [ConfigKeys.mail_signature]: '',
      [ConfigKeys.mail_headline]: '',
      [ConfigKeys.mail_footer]: ''
    });


    onMounted(() => {
      getAppConfig()
        .then(res => {
          res.map(config => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            formValues.value[config.key as any] = config.value;
          });
        });
    });


    const formatError = (errorMessage: string) => {
      return errorMessage
        .replace('0.value', 'mail host')
        .replace('3.value', 'mail port')
        .replace('1.value', 'mail from address')
        .replace('2.value', 'mail password')
        .replace('4.value', 'mail username')
        .replace('5.value', 'mail from name')
        .replace('5.key', 'mail from name')
        .replace('6.value', 'mail encryption')
        .replace('6.key', 'mail encryption')
        .replace('7.value', 'mail signature')
        .replace('7.key', 'mail signature')
        .replace('8.value', 'mail headline')
        .replace('8.key', 'mail headline')
        .replace('9.value', 'mail footer')
        .replace('9.key', 'mail footer');
    };

    const testEmailConfig = () => {
      if (isRequestingTestEmail.value === true) return;
      isRequestingTestEmail.value = true;
      testMailConfigApi(testEmail.value)
        .then(() => {
          notification.success({
            message: 'Success!',
            description: 'Mail sent successfully',
            duration: 8
          });
        })
        .catch((error) => {
          let errorStr = error.response.data.message || 'Something wen wrong, please check your mail config' as string;
          notification.error({
            message: 'Error!',
            description: errorStr,
            duration: 8
          });

        })
        .finally(() => {
          isRequestingTestEmail.value = false;
        });
    };


    const saveEmailConfiguration = () => {
      const payload: CreateUpdateAppConfig = [];
      [ConfigKeys.mail_host,
        ConfigKeys.mail_from_address,
        ConfigKeys.mail_password,
        ConfigKeys.mail_port,
        ConfigKeys.mail_username,
        ConfigKeys.mail_encryption].map((key) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        payload.push({ key, value: formValues.value[key] });
      });

      createUpdateAppConfig(payload)
        .then(() => {
          notification.success({
            message: 'Success!',
            description: 'Email configuration has been saved successfully',
            duration: 8
          });
        })
        .catch(error => {
          let errorStr = error.response.data.message as string;
          const errorMessage = formatError(errorStr);
          notification.error({
            message: 'Error!',
            description: errorMessage,
            duration: 8
          });
        });

    };

    const saveCustomizedEmailDesign = () => {
      const payload: CreateUpdateAppConfig = [];
      [ConfigKeys.mail_from_name,
        ConfigKeys.mail_signature,
        ConfigKeys.mail_headline,
        ConfigKeys.mail_footer].map((key) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        payload.push({ key, value: formValues.value[key] });
      });

      createUpdateAppConfig(payload)
        .then(() => {
          notification.success({
            message: 'Success!',
            description: 'Customized email design has been saved successfully',
            duration: 8
          });
        })
        .catch(error => {
          let errorStr = error.response.data.message as string;
          const errorMessage = formatError(errorStr);
          notification.error({
            message: 'Error!',
            description: errorMessage,
            duration: 8
          });
        });
    };

    return {
      formValues,
      testEmailConfig,
      testEmail,
      isRequestingTestEmail,
      isEmailPasswordVisible,
      togglePasswordViewMode,
      activeTab,
      saveEmailConfiguration,
      saveCustomizedEmailDesign,
      isEmailConfigurationCompleted,
      navButtonClass,
      toggleSidebar,
      inputClass,
    };
  }
});
